/* =========================================================
   Affiche PLONGÉE & APNÉE — le profil, le carnet, l'apnée, le site,
   les brevets.
   API : SNPlProfil.render(data, opts) -> string SVG
     data : { mode, titre, kicker, club, lieu, joueur, equipe,
              mode 'profil'  → p1..p8 (« 18|38 ») : minute, profondeur en mètres
              mode 'carnet'  → c1..c8 (« 142|Calanque d'En-Vau|38 m|42 min »)
              mode 'apnee'   → a1..a4 (« 6'42|Statique »)
              mode 'site'    → l1..l8 (« Libellé|valeur »)
              mode 'brevets' → b1..b8 (« 2024|Niveau 2|FFESSM ») }
     opts : { palette }

   LE PROFIL EST TRACÉ, PAS MODÉLISÉ. Le client saisit les points relevés sur
   son ordinateur ; rien n'est reconstruit à partir d'une vitesse de remontée
   supposée. Une affiche qui inventerait la forme de la courbe à partir de deux
   chiffres mentirait à la seule personne capable de s'en apercevoir.

   Auto-suffisant (aucun fetch, aucune image, aucun aléa) → re-render serveur.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    abysse: { label:'Abysse', bg:'#05161F', ink:'#EAF3F4', muted:'#79A0A8', accent:'#3FC4C9', line:'#123742', card:'#0C2530', eau:'#0E3D4B' },
    lagon:  { label:'Lagon',  bg:'#0A2E3D', ink:'#EFF7F6', muted:'#84B3B8', accent:'#F2B33D', line:'#144655', card:'#0F3A4A', eau:'#14556A' },
    craie:  { label:'Craie',  bg:'#F5F6F3', ink:'#0A2E3D', muted:'#7B8B90', accent:'#0E7C86', line:'#DFE4E1', card:'#FFFFFF', eau:'#CDE6EA' },
    nuit:   { label:'Nuit',   bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', accent:'#FF9E5C', line:'#1E4272', card:'#12355F', eau:'#123E70' },
  };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const num = s => { const m = String(s == null ? '' : s).replace(',', '.').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };

  /** « 18|38 » → { t: 18, d: 38 }. Les points sont triés par temps croissant. */
  function releves(data) {
    const l = [];
    for (let i = 1; i <= 8; i++) {
      const raw = String(data['p' + i] || '').trim(); if (!raw) continue;
      const [a, b] = raw.split('|').map(s => (s || '').trim());
      const t = num(a), d = num(b);
      if (t != null && d != null && t >= 0 && d >= 0) l.push({ t, d });
    }
    return l.sort((x, y) => x.t - y.t);
  }

  /* Un palier rond au-dessus de la valeur : 38 m → axe à 40, 17 m → 20. */
  const arrondi = v => { const pas = v <= 12 ? 3 : v <= 30 ? 10 : v <= 60 ? 20 : 25; return Math.max(pas, Math.ceil(v / pas) * pas); };

  function profil(l, p) {
    const X0 = MX + 46, X1 = PW - MX - 10, Y0 = 316, Y1 = 826;
    const tMax = Math.max.apply(null, l.map(r => r.t)) || 1;
    const dMax = arrondi(Math.max.apply(null, l.map(r => r.d)) || 10);
    const px = t => X0 + (X1 - X0) * (t / tMax);
    const py = d => Y0 + (Y1 - Y0) * (d / dMax);

    let g = '';
    /* Graduation en profondeur. L'axe descend, comme la plongée. */
    const pasG = dMax <= 12 ? 3 : dMax <= 30 ? 10 : dMax <= 60 ? 10 : 25;
    for (let d = 0; d <= dMax + 0.001; d += pasG) {
      const y = py(d);
      g += `<line x1="${X0}" y1="${y.toFixed(1)}" x2="${X1}" y2="${y.toFixed(1)}" stroke="${p.line}" stroke-width="1.2"/>`
        + T(X0 - 12, y + 5, 12, p.muted, `${d} M`, { w:600, ls:1.2, a:'end' });
    }
    /* La masse d'eau sous la courbe : c'est elle qui fait lire le graphique
       comme une plongée et non comme une courbe de bourse à l'envers. */
    g += `<path d="M ${px(l[0].t).toFixed(1)} ${py(0).toFixed(1)} `
      + l.map(r => `L ${px(r.t).toFixed(1)} ${py(r.d).toFixed(1)}`).join(' ')
      + ` L ${px(l[l.length-1].t).toFixed(1)} ${py(0).toFixed(1)} Z" fill="${p.eau}" opacity="0.85"/>`;
    g += `<polyline points="${l.map(r => `${px(r.t).toFixed(1)},${py(r.d).toFixed(1)}`).join(' ')}" fill="none" stroke="${p.accent}" stroke-width="3.5" stroke-linejoin="round" stroke-linecap="round"/>`;
    for (const r of l) g += `<circle cx="${px(r.t).toFixed(1)}" cy="${py(r.d).toFixed(1)}" r="4.5" fill="${p.bg}" stroke="${p.accent}" stroke-width="2.5"/>`;

    /* LE FOND, nommé au MILIEU du palier et non sur son premier point. Une
       plongée passe plusieurs minutes à sa profondeur maximale : marquer le
       point d'entrée collait l'étiquette contre l'axe des profondeurs, où elle
       recouvrait la graduation. Le milieu du palier est aussi l'endroit que
       l'œil regarde. */
    const dFond = Math.max.apply(null, l.map(r => r.d));
    const auFond = l.filter(r => r.d === dFond);
    const tFond = (auFond[0].t + auFond[auFond.length - 1].t) / 2;
    for (const r of auFond) g += `<circle cx="${px(r.t).toFixed(1)}" cy="${py(r.d).toFixed(1)}" r="8" fill="${p.accent}"/>`;
    g += T(px(tFond), py(dFond) - 20, 20, p.ink, `${dFond} M`, { f:'Oswald', w:700 });
    g += T((X0 + X1) / 2, Y1 + 42, 11.5, p.muted, `DURÉE ${tMax} MIN · RELEVÉS DE L'ORDINATEUR`, { w:600, ls:2.6 });
    return g;
  }

  function stats(p, items, y) {
    const it = (items || []).filter(x => x && String(x[0] || '').trim());
    if (!it.length) return '';
    const x0 = 54, w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 38, 0.56, 0, 17), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }

  /* Filigrane : un bloc et sa robinetterie. Écarté de la gamme « Mon
     matériel » faute d'être personnel, il fait très bien l'affaire ici : un
     filigrane doit être reconnaissable, pas remarquable. */
  function filigrane(p) {
    const cx = 400, w = 112, y0 = 330, yB = 806;
    return `<g opacity="0.055"><path d="M ${cx - w} ${yB} L ${cx - w} ${(y0 + w * 0.6).toFixed(1)} Q ${cx - w} ${y0} ${(cx - w * 0.32).toFixed(1)} ${(y0 - w * 0.10).toFixed(1)}`
      + ` L ${(cx + w * 0.32).toFixed(1)} ${(y0 - w * 0.10).toFixed(1)} Q ${cx + w} ${y0} ${cx + w} ${(y0 + w * 0.6).toFixed(1)} L ${cx + w} ${yB} Z" fill="${p.ink}"/>`
      + `<rect x="${(cx - w * 0.17).toFixed(1)}" y="${(y0 - w * 0.48).toFixed(1)}" width="${(w * 0.34).toFixed(1)}" height="${(w * 0.38).toFixed(1)}" fill="${p.ink}"/>`
      + `<rect x="${(cx - w * 0.58).toFixed(1)}" y="${(y0 - w * 0.44).toFixed(1)}" width="${(w * 0.41).toFixed(1)}" height="${(w * 0.16).toFixed(1)}" rx="5" fill="${p.ink}"/></g>`;
  }

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.abysse;
    const mode = String(data.mode || 'profil').toLowerCase();
    const cx = PW / 2;

    const DEF = { profil: 'LE PROFIL', carnet: 'MON CARNET', apnee: "L'APNÉE", site: 'LE SITE', brevets: 'MES BREVETS' };
    const titre = up(data.titre) || DEF[mode] || 'LE PROFIL';
    const ts = fit(titre, 690, 74, 0.54, 2, 34);
    const kicker = up(data.kicker) || up(data.club) || 'PLONGÉE';
    const sous = [up(data.club), up(data.lieu)].filter(Boolean).join(' · ');

    let corps = '', barre = '', pied = 0;

    if (mode === 'profil') {
      const l = releves(data);
      if (l.length >= 2) {
        corps = profil(l, p);
        const fond = Math.max.apply(null, l.map(r => r.d));
        const duree = Math.max.apply(null, l.map(r => r.t));
        barre = stats(p, [[fond + ' m', 'Profondeur max'], [duree + ' min', 'Durée'], [String(l.length), 'Points relevés']], 962);
      } else corps = T(cx, 600, 20, p.muted, 'DEUX POINTS SUFFISENT : LA MINUTE, PUIS LA PROFONDEUR', { w:600, ls:1.5 });
      pied = 1030;

    } else if (mode === 'carnet') {
      const l = [];
      for (let i = 1; i <= 8; i++) {
        const raw = String(data['c' + i] || '').trim(); if (!raw) continue;
        const c = raw.split('|').map(s => (s || '').trim());
        if (c[1]) l.push(c);
      }
      if (l.length) {
        const zone = 620, pas = Math.min(102, zone / l.length), y0 = 340 + (zone - pas*l.length) / 2;
        l.forEach((r, i) => {
          const y = y0 + i * pas;
          corps += (r[0] ? T(MX, y - 12, 13, p.accent, 'PLONGÉE ' + esc(up(r[0])), { w:700, ls:2, a:'start' }) : '')
            + T(MX, y + 16, fit(r[1], 420, 26, 0.60, 0, 15), p.ink, esc(r[1]), { f:'Oswald', w:700, a:'start' })
            + (r[2] ? T(PW - MX - 96, y + 16, 28, p.ink, esc(r[2]), { f:'Oswald', w:700, a:'end' }) : '')
            + (r[3] ? T(PW - MX, y + 15, 20, p.muted, esc(r[3]), { f:'Oswald', w:700, a:'end' }) : '');
          if (i < l.length - 1) corps += `<line x1="${MX}" y1="${(y + pas/2 - 7).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 7).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : NUMÉRO, SITE, PROFONDEUR, DURÉE', { w:600, ls:2 });
      pied = 1000;

    } else if (mode === 'apnee') {
      /* Trois ou quatre performances, une par bandeau pleine largeur. Les
         disciplines de l'apnée ne se comparent pas entre elles — un temps, une
         distance et une profondeur — donc pas de grille, pas de hiérarchie. */
      const l = [];
      for (let i = 1; i <= 4; i++) {
        const raw = String(data['a' + i] || '').trim(); if (!raw) continue;
        const [v, lab] = raw.split('|').map(s => (s || '').trim());
        if (v) l.push([v, lab || '']);
      }
      if (l.length) {
        const bh = Math.min(180, 620 / l.length), y0 = 316 + (620 - bh * l.length) / 2;
        l.forEach((r, i) => {
          const y = y0 + i * bh;
          corps += `<rect x="${MX}" y="${y.toFixed(1)}" width="${PW-2*MX}" height="${(bh-16).toFixed(1)}" rx="16" fill="${p.card}"/>`
            + T(cx, y + bh*0.52, fit(r[0], PW - 2*MX - 60, 70, 0.56, 0, 26), p.ink, esc(r[0]), { f:'Oswald', w:700 })
            + (r[1] ? T(cx, y + bh*0.76, 13, p.accent, esc(up(r[1])), { w:700, ls:5 }) : '');
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : LA PERFORMANCE, UNE BARRE VERTICALE, LA DISCIPLINE', { w:600, ls:1.2 });
      pied = 1000;

    } else if (mode === 'site') {
      const l = [];
      for (let i = 1; i <= 8; i++) {
        const raw = String(data['l' + i] || '').trim(); if (!raw) continue;
        const [lab, v] = raw.split('|').map(s => (s || '').trim());
        if (lab && v) l.push([lab, v]);
      }
      if (l.length) {
        const zone = 600, pas = Math.min(96, zone / l.length), y0 = 308 + (zone - pas*l.length) / 2;
        l.forEach((r, i) => {
          const y = y0 + i * pas, dernier = i === l.length - 1;
          corps += T(MX, y, 17, p.muted, esc(up(r[0])), { w:600, ls:2, a:'start' })
            + T(PW - MX, y + 8, fit(r[1], 440, 42, 0.58, 0, 18), dernier ? p.accent : p.ink, esc(r[1]), { f:'Oswald', w:700, a:'end' });
          if (!dernier) corps += `<line x1="${MX}" y1="${(y + pas/2 - 14).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 14).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : LIBELLÉ, BARRE VERTICALE, VALEUR', { w:600, ls:2 });
      pied = 976;

    } else { /* brevets */
      const l = [];
      for (let i = 1; i <= 8; i++) {
        const raw = String(data['b' + i] || '').trim(); if (!raw) continue;
        const c = raw.split('|').map(s => (s || '').trim());
        if (c[1]) l.push(c);
      }
      if (l.length) {
        const zone = 600, pas = Math.min(96, zone / l.length), y0 = 330 + (zone - pas*l.length) / 2;
        const xJ = MX + 28;
        /* le fil vertical qui relie les brevets : une progression, pas une liste */
        corps += `<line x1="${xJ}" y1="${(y0 - 6).toFixed(1)}" x2="${xJ}" y2="${(y0 + pas*(l.length-1) + 6).toFixed(1)}" stroke="${p.line}" stroke-width="2"/>`;
        l.forEach((r, i) => {
          const y = y0 + i * pas, dernier = i === l.length - 1;
          corps += `<circle cx="${xJ}" cy="${y.toFixed(1)}" r="${dernier ? 9 : 6}" fill="${dernier ? p.accent : p.bg}" stroke="${dernier ? p.accent : p.muted}" stroke-width="2.5"/>`
            + (r[0] ? T(xJ + 26, y - 8, 13, p.muted, esc(up(r[0])), { w:600, ls:2.5, a:'start' }) : '')
            + T(xJ + 26, y + 20, fit(r[1], 420, 30, 0.58, 0, 16), p.ink, esc(r[1]), { f:'Oswald', w:700, a:'start' })
            + (r[2] ? T(PW - MX, y + 10, 15, p.muted, esc(up(r[2])), { w:600, ls:2, a:'end' }) : '');
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : ANNÉE, BREVET, ORGANISME', { w:600, ls:2 });
      pied = 1000;
    }

    /* Le filigrane n'est posé que sur les modes où la place est vraiment
       vide : là où un dessin occupe déjà l'affiche, il ne ferait que salir. */
    const fili = ["carnet","apnee","site","brevets"].indexOf(mode) >= 0 ? filigrane(p) : '';
    const bas = [esc(up(data.joueur)), esc(up(data.equipe))].filter(Boolean).join(' · ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${fili}
${corps}
${barre}
${pied && bas ? T(cx, pied, fit(bas, 690, 19, 0.60, 1, 12), p.ink, bas, { f:'Oswald', w:700, ls:1 }) : ''}
</svg>`;
  }

  window.SNPlProfil = { render, PALETTES };
})();
